import React, { useState, useEffect } from 'react';
import {
    Box, Card, CardContent, Typography, Switch, TextField, Button,
    FormControlLabel, Grid, Divider, Alert, CircularProgress, Paper
} from '@mui/material';
import {
    Build as BuildIcon,
    Save as SaveIcon,
    Warning as WarningIcon,
    Schedule as ScheduleIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const MaintenanceSettings = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        maintenanceMode: false,
        maintenanceTitle: 'System Under Maintenance',
        maintenanceMessage: 'We are currently performing scheduled maintenance. The system will be back online shortly. Thank you for your patience.',
        maintenanceEndTime: '',
        allowAdminAccess: true,
        showCountdown: true
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await api.get('/school-settings');
            const data = res.data.data || {};
            // Parse maintenance settings from school settings
            if (data.maintenanceMode !== undefined) {
                setSettings(prev => ({
                    ...prev,
                    maintenanceMode: data.maintenanceMode === 'true' || data.maintenanceMode === true,
                    maintenanceTitle: data.maintenanceTitle || prev.maintenanceTitle,
                    maintenanceMessage: data.maintenanceMessage || prev.maintenanceMessage,
                    maintenanceEndTime: data.maintenanceEndTime || '',
                    allowAdminAccess: data.allowAdminAccess !== 'false',
                    showCountdown: data.showCountdown !== 'false'
                }));
            }
        } catch (e) {
            console.log('No maintenance settings found, using defaults');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put('/school-settings/bulk', {
                settings: {
                    maintenanceMode: String(settings.maintenanceMode),
                    maintenanceTitle: settings.maintenanceTitle,
                    maintenanceMessage: settings.maintenanceMessage,
                    maintenanceEndTime: settings.maintenanceEndTime || '',
                    allowAdminAccess: String(settings.allowAdminAccess),
                    showCountdown: String(settings.showCountdown)
                }
            });
            toast.success(settings.maintenanceMode ? 'Maintenance mode ENABLED' : 'Maintenance mode DISABLED');
        } catch (e) {
            toast.error('Failed to save maintenance settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <BuildIcon sx={{ fontSize: 32, color: '#ff9800' }} />
                <Box>
                    <Typography variant="h5" fontWeight={700}>Maintenance Mode</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Control system availability and display maintenance messages to users
                    </Typography>
                </Box>
            </Box>

            {/* Status Alert */}
            {settings.maintenanceMode && (
                <Alert severity="warning" sx={{ mb: 3 }}
                    icon={<WarningIcon />}
                >
                    <strong>Maintenance Mode is currently ACTIVE.</strong> Non-admin users will see the maintenance page instead of the application.
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Toggle Card */}
                <Grid item xs={12}>
                    <Card sx={{
                        border: settings.maintenanceMode ? '2px solid #ff9800' : undefined,
                        background: settings.maintenanceMode ? 'linear-gradient(135deg, #fff3e0 0%, #ffffff 100%)' : undefined
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>
                                        Enable Maintenance Mode
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        When enabled, all non-admin users will be redirected to a maintenance page
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.maintenanceMode}
                                            onChange={(e) => setSettings(prev => ({ ...prev, maintenanceMode: e.target.checked }))}
                                            color="warning"
                                            size="medium"
                                        />
                                    }
                                    label={settings.maintenanceMode ? 'ON' : 'OFF'}
                                    labelPlacement="start"
                                    sx={{ '& .MuiTypography-root': { fontWeight: 700, color: settings.maintenanceMode ? '#e65100' : '#999' } }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Settings Card */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                Maintenance Page Content
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Maintenance Title"
                                        value={settings.maintenanceTitle}
                                        onChange={(e) => setSettings(prev => ({ ...prev, maintenanceTitle: e.target.value }))}
                                        placeholder="System Under Maintenance"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Maintenance Message"
                                        value={settings.maintenanceMessage}
                                        onChange={(e) => setSettings(prev => ({ ...prev, maintenanceMessage: e.target.value }))}
                                        placeholder="Describe the maintenance activity..."
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="datetime-local"
                                        label="Expected End Time"
                                        value={settings.maintenanceEndTime}
                                        onChange={(e) => setSettings(prev => ({ ...prev, maintenanceEndTime: e.target.value }))}
                                        InputLabelProps={{ shrink: true }}
                                        helperText="When maintenance is expected to end"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.allowAdminAccess}
                                                    onChange={(e) => setSettings(prev => ({ ...prev, allowAdminAccess: e.target.checked }))}
                                                    size="small"
                                                />
                                            }
                                            label="Allow Admin Access during maintenance"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.showCountdown}
                                                    onChange={(e) => setSettings(prev => ({ ...prev, showCountdown: e.target.checked }))}
                                                    size="small"
                                                />
                                            }
                                            label="Show countdown timer"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Preview */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <VisibilityIcon sx={{ fontSize: 20, color: '#666' }} />
                                <Typography variant="subtitle1" fontWeight={700}>Preview</Typography>
                            </Box>
                            <Divider sx={{ mb: 3 }} />

                            <Paper sx={{
                                p: 4,
                                textAlign: 'center',
                                background: 'linear-gradient(135deg, #1a2332 0%, #2c3e50 100%) !important',
                                color: '#fff !important',
                                borderRadius: 3,
                                minHeight: 250,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2
                            }}>
                                <BuildIcon sx={{ fontSize: 56, color: '#ff9800', mb: 1 }} />
                                <Typography variant="h4" fontWeight={700} sx={{ color: '#fff !important' }}>
                                    {settings.maintenanceTitle || 'System Under Maintenance'}
                                </Typography>
                                <Typography variant="body1" sx={{ maxWidth: 500, opacity: 0.85, color: '#ccc !important' }}>
                                    {settings.maintenanceMessage || 'We are performing scheduled maintenance.'}
                                </Typography>
                                {settings.maintenanceEndTime && settings.showCountdown && (
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', gap: 1, mt: 2,
                                        background: 'rgba(255,152,0,0.2)', px: 3, py: 1, borderRadius: 2
                                    }}>
                                        <ScheduleIcon sx={{ color: '#ff9800' }} />
                                        <Typography variant="body2" sx={{ color: '#ff9800 !important' }}>
                                            Expected Back: {new Date(settings.maintenanceEndTime).toLocaleString('en-IN')}
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                            size="large"
                            sx={{
                                px: 4,
                                background: settings.maintenanceMode
                                    ? 'linear-gradient(135deg, #e65100 0%, #ff9800 100%) !important'
                                    : undefined
                            }}
                        >
                            {saving ? 'Saving...' : settings.maintenanceMode ? 'Enable Maintenance Mode' : 'Save Settings'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default MaintenanceSettings;
